import React from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { googleAiVoiceStyles } from '../GoogleAiVoiceStyle';
import { GoogleTtsModel, GoogleTtsModelOption, GoogleVoiceOption, LanguageOption } from '../types';
import WikiHelpIcon from '../../../wiki/WikiHelpIcon';
import GoogleVoicePreviewButton from './GoogleVoicePreviewButton';

interface GoogleOutputPanelProps {
  modelOptions: GoogleTtsModelOption[];
  selectedModel: GoogleTtsModel;
  onModelChange: (model: GoogleTtsModel) => void;
  languageOptions: LanguageOption[];
  selectedLanguage?: LanguageOption;
  onLanguageChange: (languageCode: string) => void;
  multiSpeaker: boolean;
  onMultiSpeakerChange: (checked: boolean) => void;
  voiceOptions: GoogleVoiceOption[];
  selectedVoiceA?: GoogleVoiceOption;
  selectedVoiceB?: GoogleVoiceOption;
  onVoiceAChange: (voice: GoogleVoiceOption) => void;
  onVoiceBChange: (voice: GoogleVoiceOption) => void;
  onGenerate: () => void;
  onApply: () => void;
  canGenerate: boolean;
  canApply: boolean;
  loading: boolean;
  applying: boolean;
  disabled: boolean;
  validationError: string | null;
  draftError: string | null;
  error: string | null;
  voiceError: string | null;
}

export const formatGoogleVoiceOptionLabel = (voice: GoogleVoiceOption) => {
  const gender = voice.gender.trim();
  return gender ? `${voice.displayName} (${gender})` : voice.displayName;
};

export default function GoogleOutputPanel({
  modelOptions,
  selectedModel,
  onModelChange,
  languageOptions,
  selectedLanguage,
  onLanguageChange,
  multiSpeaker,
  onMultiSpeakerChange,
  voiceOptions,
  selectedVoiceA,
  selectedVoiceB,
  onVoiceAChange,
  onVoiceBChange,
  onGenerate,
  onApply,
  canGenerate,
  canApply,
  loading,
  applying,
  disabled,
  validationError,
  draftError,
  error,
  voiceError,
}: GoogleOutputPanelProps) {
  const theme = useTheme();
  const baseDisabled = disabled || voiceOptions.length < 1 || !selectedLanguage;

  const renderVoiceAutocomplete = (label: string, value: GoogleVoiceOption | undefined, onChange: (voice: GoogleVoiceOption) => void) => (
    <div style={googleAiVoiceStyles.outputControlRow(theme)}>
      <Autocomplete
        options={voiceOptions}
        getOptionLabel={(option) => formatGoogleVoiceOptionLabel(option)}
        isOptionEqualToValue={(option, selected) => option.voiceId === selected.voiceId}
        value={value}
        onChange={(_event, newValue) => {
          if (!newValue) return;
          onChange(newValue);
        }}
        renderOption={(props, option) => (
          <li {...props} key={`${option.languageCode}-${option.voiceId}`}>
            {formatGoogleVoiceOptionLabel(option)}
          </li>
        )}
        renderInput={(params) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              {...params}
              label={label}
              variant='standard'
              sx={{width:330}}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    <InputAdornment position='end' sx={{ marginRight: 0.25 }}></InputAdornment>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
            <GoogleVoicePreviewButton key={`${selectedLanguage?.code ?? 'none'}-${value?.voiceId ?? 'none'}`} languageCode={selectedLanguage?.code} voice={value} disabled={baseDisabled} />
          </div>
        )}
        disableClearable
        sx={{ width: '100%' }}
        disabled={baseDisabled}
      />
    </div>
  );

  return (
    <div style={googleAiVoiceStyles.outputPanel(theme)}>
      <div style={googleAiVoiceStyles.outputColumn(theme)}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant='body1' fontWeight={500}>
            Output
          </Typography>
          <WikiHelpIcon tooltipId={'AiVoice'} />
        </div>

        <FormControlLabel
          sx={{ margin: 0, paddingLeft: 0 }}
          control={<Switch checked={Boolean(multiSpeaker)} onChange={(event) => onMultiSpeakerChange(event.target.checked)} />}
          label='Dialogue'
          disabled={disabled}
        />
        <div style={googleAiVoiceStyles.outputControlRow(theme)}>
          <TextField
            select
            variant='standard'
            size='small'
            value={selectedModel}
            onChange={(event) => onModelChange(event.target.value as GoogleTtsModel)}
            disabled={disabled}
              sx={{width:330}}
            label='Quality'
          >
            {modelOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <div style={googleAiVoiceStyles.outputControlRow(theme)}>
          <Autocomplete
            options={languageOptions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, selected) => option.code === selected.code}
            value={selectedLanguage}
            onChange={(_event, newValue) => {
              if (!newValue) return;
              onLanguageChange(newValue.code);
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.code}>
                {option.label}
              </li>
            )}
            renderInput={(params) => <TextField {...params} label='Language' variant='standard' />}
            disableClearable
              sx={{width:330}}
            disabled={disabled || languageOptions.length < 1}
          />
        </div>

        {!multiSpeaker ? (
          renderVoiceAutocomplete('Voice', selectedVoiceA, onVoiceAChange)
        ) : (
          <>
            {renderVoiceAutocomplete('Voice #1', selectedVoiceA, onVoiceAChange)}
            {renderVoiceAutocomplete('Voice #2', selectedVoiceB, onVoiceBChange)}
          </>
        )}
      </div>

      <div>
        {(validationError || draftError || error || voiceError) && <Alert severity='warning'>{validationError ?? draftError ?? error ?? voiceError}</Alert>}
        <div style={googleAiVoiceStyles.actionRow(theme)}>
          <Button color='primary' variant='contained' style={googleAiVoiceStyles.actionButtonPrimary} onClick={onGenerate} disabled={loading || !canGenerate || disabled}>
            GENERATE AI VOICE
          </Button>
          <Button variant='newSecondary' style={googleAiVoiceStyles.actionButtonSecondary} disabled={!canApply || loading || applying || disabled} onClick={onApply}>
            APPLY TO PAGE
          </Button>
        </div>
      </div>
    </div>
  );
}
